import React, { type FC } from 'react'

import debounce from 'lodash.debounce'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  FlexItem,
  FormElement,
  GRID_VARIANT_COLLAPSED,
  Grid,
  GridCell,
  InputNumber,
  InputText,
  MODAL_SIZE_XXL,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Select,
  TEXT_VARIANT_SMALL_DANGER,
  TEXT_VARIANT_STANDARD,
  TITLE_VARIANT_L,
  TITLE_VARIANT_S,
  Text,
  Title,
} from 'mailup-web-components'

import { MAX_SEGMENTATION_GROUPS } from 'Pages/RecipientsList/model'
import {
  modalAddSegmentCancelCallback,
  modalAddSegmentChangeCallback,
  modalAddSegmentConfirmCallback,
  modalAddSegmentValidateNameCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalAddSegment'
import {
  modalAddSegmentCanAddState,
  modalAddSegmentGroupsCountState,
  modalAddSegmentMaxGroupsCountExceededState,
  modalAddSegmentState,
} from 'Pages/RecipientsList/recoil/state/modalAddSegment'

const formattingOptions = {
  linebreak: <br />,
  strong: (chunks: any) => <strong>{chunks}</strong>,
}

const ModalAddSegment: FC = () => {
  const intl = useIntl()

  const {
    show,
    name,
    nameConflict,
    recipientsCount,
    maxRecipientsInGroup,
    distMode,
  } = useRecoilValue(modalAddSegmentState)
  const canAdd = useRecoilValue(modalAddSegmentCanAddState)
  const groupsCount = useRecoilValue(modalAddSegmentGroupsCountState)
  const maxGroupsCountExceeded = useRecoilValue(
    modalAddSegmentMaxGroupsCountExceededState,
  )

  const handleCancel = useRecoilCallback(modalAddSegmentCancelCallback, [])
  const handleConfirm = useRecoilCallback(modalAddSegmentConfirmCallback, [])
  const handleChange = useRecoilCallback(modalAddSegmentChangeCallback, [])
  const validateName = debounce(
    useRecoilCallback(modalAddSegmentValidateNameCallback, []),
    100,
  )

  //
  const nameHint = nameConflict
    ? intl.formatMessage({ id: 'Load.RecipientsList.SegmentGroupNameConflict' })
    : undefined
  const nameFeedback =
    name !== null && name.length === 0
      ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
      : undefined

  const nameFeedbackVariant = nameFeedback === undefined ? undefined : 'danger'

  //
  const isValidGroups =
    maxRecipientsInGroup !== null && maxRecipientsInGroup > 0

  const groupsHint = isValidGroups
    ? intl.formatMessage(
        { id: 'Load.RecipientsList.SegmentGroupsCountPreview' },
        {
          N: groupsCount !== undefined ? intl.formatNumber(groupsCount) : '',
        },
      )
    : undefined
  const groupsFeedback =
    maxRecipientsInGroup !== null && maxRecipientsInGroup < 1
      ? intl.formatMessage({
          id: 'Load.RecipientsList.SegmentRecipientsPerGroupError',
        })
      : undefined
  const groupsFeedbackVariant =
    groupsFeedback === undefined ? undefined : 'danger'

  const handleChangeName = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    void handleChange({ name: value })
  }

  const handleGroupsChange = (value: number | null): void => {
    if (value !== null) {
      void handleChange({ maxRecipientsInGroup: value })
    }
  }

  const handleDistModeChange = (value: 'Random' | 'Date'): void => {
    void handleChange({ distMode: value })
  }

  //
  const distModeOptions = [
    {
      label: intl.formatMessage({
        id: 'Load.RecipientsList.SegmentDistModeRandom',
      }),
      value: 'Random',
    },
    {
      label: intl.formatMessage({
        id: 'Load.RecipientsList.SegmentDistModeDate',
      }),
      value: 'Date',
    },
  ]

  return (
    <Modal
      size={MODAL_SIZE_XXL}
      show={show}
      onClose={handleCancel}
      data-mu-test="modal-add-segment"
      closeOnOverlayClick
      showClose
    >
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.RecipientsList.ModalAddSegmentTitle',
        })}
      />

      <ModalBody>
        <Flex flexDirection="column" gap="24px">
          <Title variant={TITLE_VARIANT_L}>
            <Flex gap="8px">
              <span>
                {intl.formatMessage({ id: 'Load.Common.Subscribers' })}
              </span>
              <span>{intl.formatNumber(recipientsCount)}</span>
            </Flex>
          </Title>

          <FormElement
            label={intl.formatMessage({
              id: 'Load.RecipientsList.SegmentName',
            })}
            hint={nameHint}
            feedback={nameFeedback}
            feedbackVariant={nameFeedbackVariant}
          >
            <Grid cols={2} variant={GRID_VARIANT_COLLAPSED}>
              <GridCell span={1}>
                <InputText
                  data-mu-test="input-name"
                  value={name}
                  onChange={handleChangeName}
                  onBlur={validateName}
                  onKeyUp={validateName}
                  placeholder={intl.formatMessage({
                    id: 'Load.RecipientsList.SegmentInputNamePlaceholder',
                  })}
                  variant={nameFeedbackVariant}
                />
              </GridCell>
            </Grid>
          </FormElement>

          <section>
            <Title variant={TITLE_VARIANT_S}>
              {intl.formatMessage({
                id: 'Load.RecipientsList.SegmentGroupsCountTitle',
              })}
            </Title>
            <Flex alignItems="flex-start" gap="8px">
              <Flex flex="0 1 auto" alignItems="center" height="36px">
                <Text variant={TEXT_VARIANT_STANDARD}>
                  {intl.formatMessage({
                    id: 'Load.RecipientsList.SegmentGroupsCountLabel',
                  })}
                </Text>
              </Flex>
              <Flex flexGrow={1} flexDirection="column" gap="4px">
                <FormElement
                  hint={groupsHint}
                  feedback={groupsFeedback}
                  feedbackVariant={groupsFeedbackVariant}
                >
                  <Flex>
                    <FlexItem flexGrow={0} flexBasis="120px">
                      <InputNumber
                        data-mu-test="input-max-recipients-in-group"
                        value={maxRecipientsInGroup}
                        variant={groupsFeedbackVariant}
                        onChange={handleGroupsChange}
                        precision={0}
                        min={1}
                      />
                    </FlexItem>
                    <FlexItem flexGrow={1} />
                  </Flex>
                </FormElement>
                {maxGroupsCountExceeded && (
                  <Text variant={TEXT_VARIANT_SMALL_DANGER}>
                    {intl.formatMessage(
                      {
                        id: 'Load.RecipientsList.ModalSegmentationMaxGroupsError',
                      },
                      {
                        ...formattingOptions,
                        COUNT: MAX_SEGMENTATION_GROUPS,
                      },
                    )}
                  </Text>
                )}
              </Flex>
            </Flex>
          </section>

          <section>
            <Title variant={TITLE_VARIANT_S}>
              {intl.formatMessage({
                id: 'Load.RecipientsList.SegmentDistTypeTitle',
              })}
            </Title>
            <Flex alignItems="flex-start" gap="8px">
              <Flex flex="0 1 auto" alignItems="center" height="36px">
                <Text variant={TEXT_VARIANT_STANDARD}>
                  {intl.formatMessage({
                    id: 'Load.RecipientsList.SegmentDistTypeLabel',
                  })}
                </Text>
              </Flex>
              <Flex flex="0 0 200px" flexDirection="column">
                <Select
                  data-mu-test="input-dist-mode"
                  searchable={false}
                  value={distMode}
                  options={distModeOptions}
                  onChange={handleDistModeChange}
                />
              </Flex>
            </Flex>
          </section>
        </Flex>
      </ModalBody>

      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          data-mu-test="cancel"
          onClick={handleCancel}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>

        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test="confirm"
          onClick={handleConfirm}
          disabled={!canAdd}
        >
          {intl.formatMessage({
            id: 'Load.Common.Create',
          })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

export default ModalAddSegment
